import { useContext, useEffect, useMemo, useRef } from 'react'
import Layout from '../../components/layout/layout'
import MainNav from '../../components/homepage/main-nav'
import IconBox from '../../components/common/icon-box'
import Button from '../../components/common/button'
import { RegisterDTOGenderEnum, RegisterDTO, RegisterDTOHeimerleMeuleCustomerEnum } from '@ringo/shared'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import ControlledDropdown from '../../components/form/controlled-dropdown'
import ControlledInputText from '../../components/form/controlled-input-text'
import { useTranslation } from '../../useTranslation'
import ControlledCheckbox from '../../components/form/controlled-checkbox'
import { useMutation } from '@tanstack/react-query'
import { useUser } from '../../common/hooks/auth/useUser'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, string, ref, boolean } from 'yup'
import { useValidationRules } from '../../common/validation-rules'
import { Country } from '@ringo/shared'
import { Validation } from '../../common/types'
import { useIamApi } from '../../common/api-context'
import { Captcha } from '../../components/common/captcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { getLangKeyFromI18n } from '../../common/helpers/getLangKeyHelper'
import { useI18n } from '../../useI18n'
import { GlobalContext } from '../../common/global-context'

const Register = () => {
    const navigate = useNavigate()
    const { isAuthenticated } = useUser()
    const { t, supportedLanguages } = useTranslation()
    const { settings } = useContext(GlobalContext)
    const { changeLanguage, language: currentAppLang } = useI18n()
    const iamApi = useIamApi()
    const validationRules = useValidationRules()
    const captchaRef = useRef<HCaptcha>(null)

    // Redirect if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated, navigate])

    type ExtendedRegisterDTO = RegisterDTO & {
        confirmPassword: string
    }

    const validationObject: Validation<ExtendedRegisterDTO> = {
        firstName: validationRules.firstName,
        lastName: validationRules.lastName,
        gender: validationRules.genders,
        companyName: validationRules.companyName,
        email: validationRules.email,
        password: validationRules.password,
        confirmPassword: string()
            .required()
            .oneOf([ref('password')], { key: 'createAccount.passwordsDontMatch' }),
        telephone: validationRules.telephone,
        street: validationRules.street,
        houseNumber: validationRules.houseNumber,
        postalCode: validationRules.postalCode,
        city: validationRules.city,
        country: validationRules.country,
        langKey: validationRules.language,
        docLangKey: validationRules.language,
        heimerleMeuleCustomer: string().oneOf(Object.values(RegisterDTOHeimerleMeuleCustomerEnum)).required(),
        acceptAGBs: boolean().required({ key: 'validations.mustAccept' }).oneOf([true], { key: 'validations.mustAccept' }),
    }

    const schema = object(validationObject)

    const { handleSubmit, control, watch, setValue } = useForm<ExtendedRegisterDTO>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            langKey: getLangKeyFromI18n(currentAppLang),
            heimerleMeuleCustomer: undefined,
        },
    })

    const heimerleMeuleCustomer = watch('heimerleMeuleCustomer')

    const formLanguageValue = watch('langKey')
    useEffect(() => {
        if (!formLanguageValue) return

        localStorage.setItem('lang', formLanguageValue.toLowerCase())
        changeLanguage(formLanguageValue.toLowerCase() as any)
    }, [formLanguageValue])

    useEffect(() => {
        const currentI18nLang = getLangKeyFromI18n(currentAppLang)
        if (currentI18nLang === formLanguageValue) return

        setValue('langKey', currentI18nLang)
    }, [currentAppLang])

    const alreadyACustomer = useMemo(
        () =>
            Object.entries(RegisterDTOHeimerleMeuleCustomerEnum).map(([label, id]) => ({
                id: id,
                label: t(label.toLowerCase() as any),
            })),
        [t]
    )

    const gendersList = useMemo(
        () =>
            Object.entries(RegisterDTOGenderEnum).map(([_label, id]) => ({
                id: id,
                label: t(`option.gender.${id}`),
            })),
        [t]
    )

    const countriesList = useMemo(
        () =>
            Object.entries(Country)
                .map(([_, label]) => ({
                    id: label,
                    label: t(`countries.${label}`),
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [t]
    )

    const registerUser = useMutation({
        mutationFn: (formData: ExtendedRegisterDTO) => {
            return iamApi.register(formData)
        },
        onSuccess: () => {
            navigate('/auth/register-success')
        },
    })

    const onSubmit: SubmitHandler<ExtendedRegisterDTO> = async (data) => {
        // Check for some basic validations
        if (data.password !== data.confirmPassword) {
            toast.error(t('createAccount.passwordsDontMatch'))
            return
        }

        const { response } = await captchaRef.current!.execute({ async: true })

        registerUser.mutate({ ...data, hcaptchaPasscode: response })
    }

    const supportedDocLanguages = useMemo(() => {
        return supportedLanguages.filter((x) => x.id === 'EN' || x.id === 'DE')
    }, [supportedLanguages])

    const uiSupportedLanguages = useMemo(() => {
        return supportedLanguages.filter((x) => settings.activeLanguages?.includes(x.id as any)).map((x) => ({ ...x, id: x.id }))
    }, [supportedLanguages, settings])

    return (
        <Layout>
            <MainNav />
            <div className="my-16 w-4/6 max-tablet:w-2/3 mx-auto">
                <form className="w-4/5 mx-auto max-tablet:w-11/12" onSubmit={handleSubmit(onSubmit)}>
                    <Captcha ref={captchaRef} />

                    <IconBox icon="address-card">
                        <div className="py-10 px-2">
                            <p className="font-bold text-xl text-left mb-3 max-tablet:mb-2.5 max-tablet:text-base">{t('login.registration')}</p>
                            <p className="text-base font-light text-left mb-7 max-tablet:mb-5 max-tablet:text-sm">
                                {t('createAccount.registrationDescription')}
                            </p>

                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 firstName">
                                    <ControlledInputText required label={t('firstName')} name="firstName" control={control} />
                                </div>
                                <div className="w-1/2 lastName">
                                    <ControlledInputText required label={t('lastName')} name="lastName" control={control} />
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 gender">
                                    <div className="text-left relative">
                                        <ControlledDropdown options={gendersList} name="gender" required label={t('gender')} control={control} />
                                    </div>
                                </div>
                                <div className="w-1/2"></div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 companyName">
                                    <ControlledInputText required label={t('company')} name="companyName" control={control} />
                                </div>
                                <div className="w-1/2 email">
                                    <ControlledInputText label={t('emailAddress')} name="email" required control={control} type="email" />
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 password">
                                    <ControlledInputText label={t('password')} required name="password" control={control} type="password" />
                                </div>
                                <div className="w-1/2 confirmPassword">
                                    <ControlledInputText label={t('confirmPassword')} required name="confirmPassword" control={control} type="password" />
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 telephone">
                                    <ControlledInputText label={t('phone')} required name="telephone" control={control} />
                                </div>
                                <div className="w-1/2 flex gap-[6px]">
                                    <div className="w-2/3 street">
                                        <ControlledInputText label={t('street')} required name="street" control={control} />
                                    </div>
                                    <div className="w-1/3 houseNumber">
                                        <ControlledInputText label={t('houseNumber')} required name="houseNumber" control={control} />
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 flex gap-[6px]">
                                    <div className="w-2/3 postalCode">
                                        <ControlledInputText label={t('postalCode')} required name="postalCode" control={control} />
                                    </div>
                                    <div className="w-2/3 city">
                                        <ControlledInputText label={t('city')} required name="city" control={control} />
                                    </div>
                                </div>
                                <div className="w-1/2 country">
                                    <ControlledDropdown label={t('country')} options={countriesList} required name="country" control={control} />
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 langKey">
                                    <div className="text-left relative">
                                        <ControlledDropdown
                                            options={uiSupportedLanguages}
                                            name="langKey"
                                            control={control}
                                            required
                                            label={t('displayLanguage')}
                                        />
                                    </div>
                                </div>
                                <div className="w-1/2 docLangKey">
                                    <div className="text-left relative">
                                        <ControlledDropdown
                                            options={supportedDocLanguages}
                                            name="docLangKey"
                                            control={control}
                                            required
                                            label={t('preferredDocumentLanguage')}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-[6px] mb-3">
                                <div className="w-1/2 heimerleMeuleCustomer">
                                    <div className="text-left relative">
                                        <ControlledDropdown
                                            options={alreadyACustomer}
                                            name="heimerleMeuleCustomer"
                                            placeholder={t('pleaseSelect')}
                                            control={control}
                                            required
                                            label={t('createAccount.areYouAlreadyACustomerOfHeimerleMeuleGmbH')}
                                        />
                                    </div>
                                </div>
                                {heimerleMeuleCustomer === RegisterDTOHeimerleMeuleCustomerEnum.Yes && (
                                    <div className="w-1/2 customerNumber">
                                        <ControlledInputText label={t('customerNumber')} name="customerNumber" control={control} />
                                    </div>
                                )}
                            </div>

                            <div className="text-left mt-6">
                                <p className="text-base font-light text-custom_yellow leading-0 block max-tablet:text-sm">* {t('mandatoryField')}</p>
                            </div>

                            <div className="mt-6 acceptAGBs">
                                <div className="flex items-center w-full">
                                    <ControlledCheckbox control={control} name="acceptAGBs" label={t('createAccount.registrationConsent') + ' **'} />
                                </div>
                                <div className="pl-7 text-left w-full mt-2 max-tablet:pl-5">
                                    <p className="text-sm font-light max-tablet:text-xs">{'** ' + t('createAccount.registrationConsentDescription')}</p>
                                </div>
                            </div>
                            <div className="flex justify-center mt-7">
                                <div className="mr-4 returnBtn">
                                    <Button type={'squareYellow'} icon="chevron-left" text={t('back')} onClick={() => navigate(-1)} preventSubmit />
                                </div>
                                <Button
                                    type={'squareBlue'}
                                    icon="address-card"
                                    text={t('homePage.createAccount')}
                                    isLoading={registerUser.isPending}
                                    className="submitBtn"
                                />
                            </div>
                        </div>
                    </IconBox>
                </form>
            </div>
        </Layout>
    )
}

export default Register
